<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import dayjs from 'dayjs';
import {
  Chart,
  DoughnutController,
  ArcElement,
  Tooltip,
  ChartConfiguration,
} from 'chart.js';
import { getHotwordAudienceProfileService } from '@/api/dp';

Chart.register(DoughnutController, ArcElement, Tooltip);

const getCssVar = (name: string, fallback: string) => {
  if (typeof document === 'undefined') return fallback;
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
};

/*
 * 観客属性チャート
 * - Gender View: 男女比ドーナツ
 * - Age View: 年齢層ドーナツ
 * - Gender x Age: 男女×年齢層 12分割ドーナツ
 */

const props = defineProps<{
  movieId?: number | string;
  duration?: any[];
  // 外部から観客属性データを渡す場合に指定（指定時は API を呼ばずにこのデータを描画する。genders/ageGroups は audienceProfile と同じ構造）
  profile?: { genders?: any[]; ageGroups?: any[] } | null;
  // 初期表示のビュー（未指定は性別×年齢層）
  initialView?: 'gender' | 'age' | 'matrix';
}>();

const AGE_ORDER = ['10代', '20代', '30代', '40代', '50代', '60代+'];
const GENDER_ORDER = ['男性', '女性'];
const MALE_SHADES = ['#bfdbfe', '#93c5fd', '#60a5fa', '#3b82f6', '#2563eb', '#1e40af'];
const FEMALE_SHADES = ['#fbcfe8', '#f9a8d4', '#f472b6', '#ec4899', '#db2777', '#be185d'];
const GENDER_COLOR: Record<string, string> = { 男性: '#0066ff', 女性: '#ec4899' };
const GENDER_SHADES: Record<string, string[]> = { 男性: MALE_SHADES, 女性: FEMALE_SHADES };
const AGE_COLORS = ['#f59e0b', '#f97316', '#fb7185', '#8b5cf6', '#14b8a6', '#0ea5e9'];

interface AgeSeg {
  ageGroup: string;
  count: number;
  percent: number;
  color: string;
}
interface GenderSeg {
  gender: string;
  count: number;
  percent: number;
  color: string;
  ageGroups: AgeSeg[];
}
interface ChartSeg {
  label: string;
  value: number;
  color: string;
}
interface AgeChartSeg extends ChartSeg {
  percent: number;
}
interface MatrixChartSeg extends ChartSeg {
  gender: string;
  ageGroup: string;
  percent: number;
}
type AudienceViewMode = 'gender' | 'age' | 'matrix';

const loading = ref(false);
const profileGenders = ref<any[]>([]);
const profileAgeGroups = ref<any[]>([]);
const viewMode = ref<AudienceViewMode>(props.initialView ?? 'matrix');

const genderSegments = computed<GenderSeg[]>(() =>
  profileGenders.value.map((g: any) => {
    const gender: string = g?.gender ?? '';
    const color = GENDER_COLOR[gender] ?? '#9ca3af';
    const shades = GENDER_SHADES[gender] ?? MALE_SHADES;
    const rawAgeGroups: any[] = g?.ageGroups ?? [];
    const ageMap = new Map(
      rawAgeGroups.map((a: any) => [String(a?.ageGroup ?? ''), a]),
    );
    const ageGroups: AgeSeg[] = AGE_ORDER.map((ageGroup, idx) => {
      const raw = ageMap.get(ageGroup);
      return {
        ageGroup,
        // count がないデータ（insight など percent のみ）は percent を描画値として使う
        count: Number(raw?.count ?? raw?.percent ?? 0),
        percent: Number(raw?.percent ?? 0),
        color: shades[idx % shades.length],
      };
    });
    return {
      gender,
      count: Number(g?.count ?? g?.percent ?? 0),
      percent: Number(g?.percent ?? 0),
      color,
      ageGroups,
    };
  }),
);

const hasData = computed(() => genderSegments.value.length > 0);

const ageSegments = computed<AgeChartSeg[]>(() => {
  const raw = Array.isArray(profileAgeGroups.value) ? profileAgeGroups.value : [];
  return raw
    .map((a: any): AgeChartSeg => ({
      label: String(a?.ageGroup ?? ''),
      value: Number(a?.count ?? a?.percent ?? 0),
      percent: Number(a?.percent ?? 0),
      color: '#9ca3af',
    }))
    .sort((a, b) => {
      const ia = AGE_ORDER.indexOf(a.label);
      const ib = AGE_ORDER.indexOf(b.label);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    })
    .map((a, idx) => ({ ...a, color: AGE_COLORS[idx % AGE_COLORS.length] }));
});

const matrixSegments = computed<MatrixChartSeg[]>(() => {
  const segs: MatrixChartSeg[] = [];
  for (const gender of GENDER_ORDER) {
    const g = genderSegments.value.find((item) => item.gender === gender);
    for (const age of g?.ageGroups ?? []) {
      segs.push({
        gender,
        ageGroup: age.ageGroup,
        label: `${gender} ${age.ageGroup}`,
        value: age.count,
        percent: age.percent,
        color: age.color,
      });
    }
    if (!g) {
      const shades = GENDER_SHADES[gender] ?? MALE_SHADES;
      AGE_ORDER.forEach((ageGroup, idx) => {
        segs.push({
          gender,
          ageGroup,
          label: `${gender} ${ageGroup}`,
          value: 0,
          percent: 0,
          color: shades[idx % shades.length],
        });
      });
    }
  }
  return segs;
});

const outerSegments = computed<ChartSeg[]>(() =>
  genderSegments.value.map((g) => ({ label: g.gender, value: g.count, color: g.color })),
);

const chartSegDatasets = computed<ChartSeg[][]>(() => {
  if (viewMode.value === 'gender') return [outerSegments.value];
  if (viewMode.value === 'age') return [ageSegments.value];
  return [matrixSegments.value];
});

const chartCanvas = ref<HTMLCanvasElement | null>(null);
let chartInstance: Chart | null = null;
let themeObserver: MutationObserver | null = null;

const getChartPadding = () => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 480;
  if (viewMode.value === 'matrix') {
    return isMobile
      ? { top: 40, right: 44, bottom: 52, left: 44 }
      : { top: 36, right: 48, bottom: 48, left: 48 };
  }
  return isMobile
    ? { top: 36, right: 18, bottom: 18, left: 18 }
    : { top: 28, right: 28, bottom: 28, left: 28 };
};

const formatPercent = (pct: number) => `${Number(pct ?? 0)}%`;

const formatPctLabel = (pct: number) => formatPercent(pct);

const getChartPercent = (dataIndex: number) => {
  if (viewMode.value === 'gender') return Number(genderSegments.value[dataIndex]?.percent ?? 0);
  if (viewMode.value === 'age') return Number(ageSegments.value[dataIndex]?.percent ?? 0);
  if (viewMode.value === 'matrix') return Number(matrixSegments.value[dataIndex]?.percent ?? 0);
  return 0;
};

const getChartSegmentLabel = (dataIndex: number) => {
  if (viewMode.value === 'gender') return genderSegments.value[dataIndex]?.gender ?? '';
  if (viewMode.value === 'age') return ageSegments.value[dataIndex]?.label ?? '';
  if (viewMode.value === 'matrix') return matrixSegments.value[dataIndex]?.label ?? '';
  return '';
};

interface ArcProps {
  x: number;
  y: number;
  startAngle: number;
  endAngle: number;
  outerRadius: number;
  innerRadius: number;
}

interface ExternalLabelLayout {
  pct: number;
  label: string;
  edgeX: number;
  edgeY: number;
  midX: number;
  midY: number;
  endX: number;
  labelY: number;
  alignRight: boolean;
}

const LABEL_LINE_HEIGHT = 13;

const resolveExternalLabelY = (
  labels: ExternalLabelLayout[],
  canvasHeight: number,
): number[] => {
  if (!labels.length) return [];
  const safeTop = 12;
  const safeBottom = canvasHeight - 12;
  const sorted = labels.map((item, idx) => ({ ...item, idx })).sort((a, b) => a.labelY - b.labelY);
  const ys = sorted.map((item) => Math.max(safeTop, Math.min(safeBottom, item.labelY)));

  for (let pass = 0; pass < 8; pass += 1) {
    for (let i = 1; i < ys.length; i += 1) {
      if (ys[i] - ys[i - 1] < LABEL_LINE_HEIGHT) {
        ys[i] = ys[i - 1] + LABEL_LINE_HEIGHT;
      }
    }
    for (let i = ys.length - 2; i >= 0; i -= 1) {
      if (ys[i + 1] - ys[i] < LABEL_LINE_HEIGHT) {
        ys[i] = ys[i + 1] - LABEL_LINE_HEIGHT;
      }
    }
    ys[0] = Math.max(safeTop, ys[0]);
    ys[ys.length - 1] = Math.min(safeBottom, ys[ys.length - 1]);
  }

  const result = new Array(labels.length).fill(0);
  sorted.forEach((item, i) => {
    result[item.idx] = ys[i];
  });
  return result;
};

const buildExternalLabel = (
  pct: number,
  p: ArcProps,
  canvasHeight: number,
): ExternalLabelLayout | null => {
  if (pct <= 0) return null;
  const midAngle = (p.startAngle + p.endAngle) / 2;
  const cos = Math.cos(midAngle);
  const sin = Math.sin(midAngle);
  const edgeX = p.x + cos * p.outerRadius;
  const edgeY = p.y + sin * p.outerRadius;
  const midX = p.x + cos * (p.outerRadius + 10);
  const midY = p.y + sin * (p.outerRadius + 10);
  const endX = p.x + cos * (p.outerRadius + 22);
  const endY = p.y + sin * (p.outerRadius + 22);
  const safeTop = 12;
  const safeBottom = canvasHeight - 12;
  return {
    pct,
    label: formatPctLabel(pct),
    edgeX,
    edgeY,
    midX,
    midY,
    endX,
    labelY: Math.max(safeTop, Math.min(safeBottom, endY)),
    alignRight: cos >= 0,
  };
};

const drawExternalLabel = (ctx: CanvasRenderingContext2D, item: ExternalLabelLayout, labelY: number) => {
  ctx.strokeStyle = getCssVar('--text-secondary', '#606266');
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(item.edgeX, item.edgeY);
  ctx.lineTo(item.midX, item.midY);
  ctx.lineTo(item.endX, labelY);
  ctx.stroke();

  ctx.fillStyle = getCssVar('--text-primary', '#303133');
  ctx.font = '700 11px sans-serif';
  ctx.textAlign = item.alignRight ? 'left' : 'right';
  ctx.textBaseline = 'middle';
  ctx.fillText(item.label, item.endX + (item.alignRight ? 3 : -3), labelY);
};

const drawAudiencePctLabel = (
  ctx: CanvasRenderingContext2D,
  pct: number,
  p: ArcProps,
) => {
  if (pct <= 0) return;
  const midAngle = (p.startAngle + p.endAngle) / 2;
  const cos = Math.cos(midAngle);
  const sin = Math.sin(midAngle);
  const label = formatPctLabel(pct);
  const angleSpan = Math.max(0, p.endAngle - p.startAngle);
  const midRadius = (p.innerRadius + p.outerRadius) / 2;
  const arcLength = angleSpan * midRadius;
  const ringThickness = p.outerRadius - p.innerRadius;

  ctx.save();
  ctx.font = '700 12px sans-serif';
  const textWidth = ctx.measureText(label).width;
  const canFitInside = pct >= 3 && textWidth + 8 <= arcLength && 14 <= ringThickness - 4;

  if (canFitInside) {
    const radius = (p.innerRadius + p.outerRadius) / 2;
    const lx = p.x + cos * radius;
    const ly = p.y + sin * radius;
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0,0,0,0.35)';
    ctx.shadowBlur = 2;
    ctx.fillText(label, lx, ly);
    ctx.restore();
    return null;
  }
  ctx.restore();
  return buildExternalLabel(pct, p, ctx.canvas.height);
};

const datalabelsPlugin = {
  id: 'audienceDatalabels',
  afterDatasetsDraw(chart: any) {
    if (!hasData.value) return;
    const ctx = chart.ctx as CanvasRenderingContext2D;
    const externalLeft: ExternalLabelLayout[] = [];
    const externalRight: ExternalLabelLayout[] = [];

    chart.data.datasets.forEach((dataset: any, dsi: number) => {
      const meta = chart.getDatasetMeta(dsi);
      meta.data.forEach((arc: any, i: number) => {
        const pct = getChartPercent(i);
        const p = arc.getProps(['x', 'y', 'startAngle', 'endAngle', 'outerRadius', 'innerRadius']);
        const external = drawAudiencePctLabel(ctx, pct, p);
        if (!external) return;
        if (external.alignRight) externalRight.push(external);
        else externalLeft.push(external);
      });
    });

    const leftYs = resolveExternalLabelY(externalLeft, ctx.canvas.height);
    const rightYs = resolveExternalLabelY(externalRight, ctx.canvas.height);
    externalLeft.forEach((item, idx) => drawExternalLabel(ctx, item, leftYs[idx]));
    externalRight.forEach((item, idx) => drawExternalLabel(ctx, item, rightYs[idx]));
  },
};

const updateChart = () => {
  const chart = chartInstance;
  const canvas = chartCanvas.value;
  if (
    !chart
    || !canvas
    || !chart.ctx
    || chart.canvas !== canvas
    || Chart.getChart(canvas) !== chart
  ) {
    if (chartInstance === chart) chartInstance = null;
    return;
  }
  const sliceBorder = getCssVar('--bg-panel', '#fff');
  const source = chartSegDatasets.value;
  chart.data.datasets = source.map((segments) => ({
    label:
      viewMode.value === 'gender'
        ? '男女比'
        : viewMode.value === 'age'
          ? '年齢層'
          : '性別×年齢層',
    data: segments.map((s) => s.value),
    backgroundColor: segments.map((s) => s.color),
    borderWidth: 2,
    borderColor: sliceBorder,
    hoverOffset: 4,
  })) as any;
  (chart.options.layout as any) = {
    padding: getChartPadding(),
  };
  chart.update();
};

const handleWindowResize = () => {
  updateChart();
};

onMounted(() => {
  if (chartCanvas.value) {
    const config: ChartConfiguration<'doughnut'> = {
      type: 'doughnut',
      data: {
        labels: [],
        datasets: [
          {
            label: '男女比',
            data: [],
            backgroundColor: [],
            borderWidth: 2,
            borderColor: getCssVar('--bg-panel', '#fff'),
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '45%',
        layout: { padding: getChartPadding() },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (items: any) => {
                const item = items[0];
                if (!item) return '';
                return getChartSegmentLabel(item.dataIndex);
              },
              label: (ctx: any) => {
                const pct = getChartPercent(ctx.dataIndex);
                return ` ${formatPercent(pct)}`;
              },
            },
          },
        },
      },
      plugins: [datalabelsPlugin],
    };
    chartInstance = new Chart(chartCanvas.value, config);
    updateChart();
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleWindowResize);
  }
  if (typeof MutationObserver !== 'undefined' && typeof document !== 'undefined') {
    themeObserver = new MutationObserver(() => updateChart());
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  }
  fetchProfile();
});

watch(
  [chartSegDatasets, viewMode, genderSegments, profileAgeGroups],
  () => {
    updateChart();
  },
  { deep: true },
);

// 外部データ指定時はそのまま反映
const applyExternalProfile = () => {
  profileGenders.value = Array.isArray(props.profile?.genders) ? props.profile!.genders! : [];
  profileAgeGroups.value = Array.isArray(props.profile?.ageGroups) ? props.profile!.ageGroups! : [];
};

watch(
  () => props.profile,
  () => {
    if (props.profile !== undefined) applyExternalProfile();
  },
  { deep: true },
);

watch(
  [() => props.movieId, () => props.duration],
  () => {
    fetchProfile();
  },
  { deep: true },
);

let fetchId = 0;
const fetchProfile = async () => {
  // 外部データ指定時は取得しない
  if (props.profile !== undefined) {
    applyExternalProfile();
    return;
  }
  if (props.movieId == null || props.movieId === '') return;
  const localId = ++fetchId;
  loading.value = true;
  try {
    const params: any = { movieId: props.movieId };
    if (
      Array.isArray(props.duration) &&
      props.duration.length >= 2 &&
      props.duration[0] &&
      props.duration[1]
    ) {
      params.dateFrom = dayjs(props.duration[0]).startOf('day').valueOf();
      params.dateTo = dayjs(props.duration[1]).add(1, 'day').startOf('day').valueOf();
    }
    const res: any = await getHotwordAudienceProfileService(params);
    if (localId !== fetchId) return;
    const payload = res?.data ?? res;
    profileGenders.value = Array.isArray(payload?.genders) ? payload.genders : [];
    profileAgeGroups.value = Array.isArray(payload?.ageGroups) ? payload.ageGroups : [];
  } catch (e) {
    if (localId !== fetchId) return;
    profileGenders.value = [];
    profileAgeGroups.value = [];
  } finally {
    if (localId === fetchId) loading.value = false;
  }
};

onBeforeUnmount(() => {
  fetchId += 1;
  themeObserver?.disconnect();
  themeObserver = null;
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', handleWindowResize);
  }
  const chart = chartInstance;
  chartInstance = null;
  if (chart?.ctx) {
    chart.stop();
    chart.destroy();
  }
});
</script>

<template>
  <div class="audience-pie">
    <div class="audience-view-switch" v-if="hasData">
      <button :class="['switch-btn', { active: viewMode === 'gender' }]" @click="viewMode = 'gender'">
        性別
      </button>
      <button :class="['switch-btn', { active: viewMode === 'age' }]" @click="viewMode = 'age'">
        年齢層
      </button>
      <button :class="['switch-btn', { active: viewMode === 'matrix' }]" @click="viewMode = 'matrix'">
        性別×年齢層
      </button>
    </div>

    <div
      class="audience-body"
      :class="{
        'audience-body--centered': hasData,
        'audience-body--matrix': hasData && viewMode === 'matrix',
      }"
    >
      <div class="audience-chart-wrap">
        <canvas ref="chartCanvas"></canvas>
        <div class="audience-placeholder" v-if="loading">読み込み中…</div>
        <div class="audience-placeholder" v-else-if="!hasData">データがありません</div>
      </div>

      <template v-if="hasData">
        <div class="audience-legend" v-if="viewMode === 'gender'">
          <div class="audience-legend-title">男女比</div>
          <div class="audience-legend-items">
            <div class="audience-legend-item" v-for="g in genderSegments" :key="g.gender">
              <span class="audience-legend-dot" :style="{ background: g.color }"></span>
              <span class="audience-legend-name">{{ g.gender }}</span>
              <span class="audience-legend-value">{{ formatPercent(g.percent ?? 0) }}</span>
            </div>
          </div>
        </div>

        <div class="audience-legend" v-if="viewMode === 'age'">
          <div class="audience-legend-title">年齢層構成</div>
          <div class="audience-legend-items">
            <div class="audience-legend-item" v-for="a in ageSegments" :key="a.label">
              <span class="audience-legend-dot" :style="{ background: a.color }"></span>
              <span class="audience-legend-name">{{ a.label }}</span>
              <span class="audience-legend-value">{{ formatPercent(a.percent ?? 0) }}</span>
            </div>
          </div>
        </div>

        <div class="audience-legend audience-legend--matrix" v-if="viewMode === 'matrix'">
          <div class="audience-legend-title">性別×年齢層</div>
          <div class="audience-matrix-legend">
            <div class="matrix-legend-group" v-for="gender in GENDER_ORDER" :key="gender">
              <div class="matrix-legend-group-title">{{ gender }}</div>
              <div class="matrix-legend-items">
                <div
                  class="audience-legend-item"
                  v-for="seg in matrixSegments.filter((s) => s.gender === gender)"
                  :key="`${seg.gender}-${seg.ageGroup}`"
                >
                  <span class="audience-legend-dot" :style="{ background: seg.color }"></span>
                  <span class="audience-legend-name">{{ seg.ageGroup }}</span>
                  <span class="audience-legend-value">{{ formatPercent(seg.percent ?? 0) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="less">
.audience-pie {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  flex: 1;
  min-height: 0;
}

.audience-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.audience-body--centered {
  flex: 1;
  justify-content: center;
  align-items: center;
}

.audience-body--matrix {
  gap: 20px;
}

.audience-body--centered .audience-chart-wrap {
  width: min(100%, 360px);
  margin: 0 auto;
}

.audience-body--centered .audience-legend {
  width: auto;
  margin: 0 auto;
}

.audience-body--centered .audience-legend-title {
  text-align: center;
}

.audience-body--centered .audience-legend-items {
  justify-content: center;
}

.audience-view-switch {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.switch-btn {
  border: 1px solid var(--border-color);
  background: var(--bg-panel);
  color: var(--text-secondary);
  border-radius: 14px;
  padding: 2px 10px;
  font-size: 0.68rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.switch-btn.active {
  border-color: var(--accent-blue);
  color: var(--accent-blue);
  background: color-mix(in srgb, var(--accent-blue) 15%, transparent);
}

.audience-chart-wrap {
  position: relative;
  width: 100%;
  height: 280px;
  flex-shrink: 0;
  overflow: visible;
  display: flex;
  align-items: center;
  justify-content: center;
}

.audience-chart-wrap canvas {
  width: 100% !important;
  height: 100% !important;
  display: block;
  margin: 0 auto;
}

.audience-placeholder {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.78rem;
  color: var(--text-tertiary);
  pointer-events: none;
}

.audience-legend {
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
}

.audience-legend--matrix {
  width: min(100%, 360px);
  margin-top: 4px;
  padding-top: 8px;
}

.audience-legend--matrix .audience-legend-title {
  text-align: center;
}

.audience-legend--matrix .audience-matrix-legend {
  margin: 0 auto;
}

.audience-legend-title {
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--text-tertiary);
}

.audience-legend-items {
  display: flex;
  flex-wrap: wrap;
  column-gap: 18px;
  row-gap: 4px;
}

.audience-legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
}

.audience-legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.audience-legend-name {
  color: var(--text-secondary);
}

.audience-legend-value {
  margin-left: 6px;
  font-weight: 600;
  color: var(--text-primary);
}

.audience-matrix-legend {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 36px;
}

.matrix-legend-group {
  width: 148px;
  flex-shrink: 0;
}

.matrix-legend-group-title {
  font-size: 0.68rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 4px;
  text-align: center;
}

.matrix-legend-items {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.matrix-legend-items .audience-legend-item {
  display: grid;
  grid-template-columns: 8px 42px 1fr;
  align-items: center;
  gap: 5px;
}

.matrix-legend-items .audience-legend-name {
  white-space: nowrap;
}

.matrix-legend-items .audience-legend-value {
  margin-left: 0;
  text-align: right;
}

@media (max-width: 480px) {
  .audience-body--centered .audience-chart-wrap {
    width: min(100%, 300px);
  }

  .audience-chart-wrap {
    height: 240px;
  }

  .audience-matrix-legend {
    gap: 24px;
  }

  .matrix-legend-group {
    width: 132px;
  }
}
</style>
